import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Loader2, BookOpen, Palette } from "lucide-react";

type RenderVariant = "story" | "coloring";

interface BookPreviewProps {
  storyId: number;
  storyTitle: string;
}

export function BookPreview({ storyId, storyTitle }: BookPreviewProps) {
  const { toast } = useToast();
  const [variant, setVariant] = useState<RenderVariant>("story");

  const downloadPdfMutation = useMutation({
    mutationFn: async (selectedVariant: RenderVariant) => {
      const response = await apiRequest(
        "POST",
        "/api/pdf/render",
        {
          storyId,
          variant: selectedVariant,
          useReactPdf: true,
        },
        { timeout: 120000 } // 2 minute timeout for PDF generation
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to generate PDF");
      }

      return response.blob();
    },
    onSuccess: (blob, selectedVariant) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${storyTitle}-${selectedVariant}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "PDF Downloaded",
        description: `Your ${selectedVariant === "story" ? "Story" : "Coloring Book"} PDF has been downloaded successfully.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Download Failed",
        description: error.message || "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleDownload = () => {
    downloadPdfMutation.mutate(variant);
  };

  return (
    <Card className="w-full" data-testid="book-preview-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          Download PDF Book
        </CardTitle>
        <CardDescription>
          Choose between a Story PDF or Coloring Book PDF to download
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Variant Toggle */}
        <Tabs 
          value={variant} 
          onValueChange={(value) => setVariant(value as RenderVariant)}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2" data-testid="variant-toggle">
            <TabsTrigger 
              value="story" 
              className="flex items-center gap-2"
              data-testid="tab-story"
              aria-label="Story PDF variant"
            >
              <BookOpen className="w-4 h-4" />
              Story PDF
            </TabsTrigger>
            <TabsTrigger 
              value="coloring" 
              className="flex items-center gap-2"
              data-testid="tab-coloring"
              aria-label="Coloring Book PDF variant"
            >
              <Palette className="w-4 h-4" />
              Coloring Book
            </TabsTrigger>
          </TabsList>

          <TabsContent value="story" className="space-y-4" data-testid="content-story">
            <div className="rounded-lg bg-gradient-to-br from-purple-50 to-teal-50 p-6 text-center space-y-3">
              <div className="flex justify-center">
                <BookOpen className="w-16 h-16 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Story PDF</h3>
              <p className="text-sm text-gray-600">
                Complete illustrated story with text and beautiful artwork - perfect for bedtime reading!
              </p>
            </div>
          </TabsContent>

          <TabsContent value="coloring" className="space-y-4" data-testid="content-coloring">
            <div className="rounded-lg bg-gradient-to-br from-pink-50 to-purple-50 p-6 text-center space-y-3">
              <div className="flex justify-center">
                <Palette className="w-16 h-16 text-pink-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900">Coloring Book PDF</h3>
              <p className="text-sm text-gray-600">
                Line art illustrations ready for coloring - unleash your child's creativity!
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Download Button */}
        <div className="flex flex-col items-center gap-3">
          <Button
            onClick={handleDownload}
            disabled={downloadPdfMutation.isPending}
            variant="gradient"
            size="lg"
            className="w-full sm:w-auto"
            data-testid="button-download-pdf"
            aria-label={`Download ${variant === "story" ? "Story" : "Coloring Book"} PDF`}
          >
            {downloadPdfMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download {variant === "story" ? "Story" : "Coloring Book"} PDF
              </>
            )}
          </Button>

          {downloadPdfMutation.isPending && (
            <p className="text-sm text-muted-foreground animate-pulse" data-testid="text-generating-status">
              This may take a minute... Please don't close this page.
            </p>
          )}
        </div>

        {/* Info Section */}
        <div className="text-xs text-muted-foreground text-center border-t pt-4">
          <p>
            Your PDF will be generated on-demand and downloaded automatically when ready.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
